import { randomInt, rollChance } from "../combat/dice.js";

/**
 * Loot-table format + resolver.
 *
 * Authored shape (`specs/inventory-and-economy.md §Loot authoring`):
 *
 * ```json
 * {
 *   "id": "goblin_basic",
 *   "guaranteed": [{ "item": "gold", "amount": "1d6" }],
 *   "rolls": [
 *     { "chance": 0.25, "drops": [{ "item": "shortsword", "quantity": 1 }] },
 *     { "chance": 0.10, "drops": [{ "item": "healing_potion_minor", "quantity": 1 }] }
 *   ],
 *   "includes": ["equipment_basic"]
 * }
 * ```
 *
 *  - `guaranteed` drops always fire.
 *  - Each `rolls` entry rolls independently against its `chance`. Hits emit
 *    every `drops` entry; misses skip the whole entry.
 *  - `includes` lets a table compose others ("goblin_basic" + "equipment_basic")
 *    so designers don't copy-paste common drops. Cycles are detected and
 *    treated as terminal — the included table is visited at most once per
 *    resolve call.
 *  - Amounts accept plain integers (`{ quantity: 1 }`) or dice notation
 *    (`{ amount: "1d6" }`). Both styles map to a post-resolution integer
 *    quantity on the output entry.
 *
 * Resolver is pure + deterministic under a seeded RNG so boss-kill replays
 * from a saved seed produce identical drops.
 */

// ── Authoring types ──────────────────────────────────────────────────────────

export interface LootDropSpec {
  /** Item ID (matches `SRDItem.index`). */
  readonly item: string;
  /** Dice notation ("1d6") — mutually exclusive with `quantity`. */
  readonly amount?: string;
  /** Flat quantity — mutually exclusive with `amount`. Defaults to 1. */
  readonly quantity?: number;
}

export interface LootRollEntry {
  /** Probability in [0, 1] that this entry fires. */
  readonly chance: number;
  readonly drops: readonly LootDropSpec[];
}

export interface LootTable {
  readonly id: string;
  readonly guaranteed?: readonly LootDropSpec[];
  readonly rolls?: readonly LootRollEntry[];
  /** IDs of other tables whose drops are appended to this one. */
  readonly includes?: readonly string[];
}

// ── Resolver output ──────────────────────────────────────────────────────────

export interface ResolvedLoot {
  readonly itemId: string;
  readonly quantity: number;
}

export type LootTableLookup = (id: string) => LootTable | undefined;

type IntRng = (min: number, max: number) => number;

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Resolve a loot table to a flat list of drops.
 *
 * `lookup` is the registry function used to walk `includes` references.
 * Default is "no tables registered" — callers can wire DataRegistry once
 * that system owns loot JSON.
 *
 * `rng` is injected so tests can hand in a deterministic source.
 */
export function resolveLootTable(
  table: LootTable,
  lookup: LootTableLookup = () => undefined,
  rng: IntRng = randomInt,
): ResolvedLoot[] {
  const seen = new Set<string>();
  return resolveTableInternal(table, lookup, rng, seen);
}

// ── Internals ────────────────────────────────────────────────────────────────

function resolveTableInternal(
  table: LootTable,
  lookup: LootTableLookup,
  rng: IntRng,
  seen: Set<string>,
): ResolvedLoot[] {
  if (seen.has(table.id)) return []; // cycle guard
  seen.add(table.id);

  const out: ResolvedLoot[] = [];

  // Guaranteed drops.
  for (const drop of table.guaranteed ?? []) {
    const qty = resolveQuantity(drop, rng);
    if (qty > 0) out.push({ itemId: drop.item, quantity: qty });
  }

  // Probabilistic rolls. `rollChance` comes from `combat/dice.ts` so
  // trap triggers, loot rolls, and other probability gates share one
  // implementation.
  for (const entry of table.rolls ?? []) {
    if (!rollChance(entry.chance, rng)) continue;
    for (const drop of entry.drops) {
      const qty = resolveQuantity(drop, rng);
      if (qty > 0) out.push({ itemId: drop.item, quantity: qty });
    }
  }

  // Composition.
  for (const includedId of table.includes ?? []) {
    const included = lookup(includedId);
    if (!included) continue;
    out.push(...resolveTableInternal(included, lookup, rng, seen));
  }

  return out;
}

function resolveQuantity(drop: LootDropSpec, rng: IntRng): number {
  if (drop.amount !== undefined) return parseAndRollDice(drop.amount, rng);
  if (drop.quantity !== undefined) return Math.max(0, Math.floor(drop.quantity));
  return 1;
}

/**
 * Parse dice notation ("2d6", "1d4") or an integer string ("3") and roll it.
 * Returns 0 for malformed input so a single bad table row doesn't kill the
 * whole drop resolution.
 */
function parseAndRollDice(notation: string, rng: IntRng): number {
  const trimmed = notation.trim();
  const asInt = Number.parseInt(trimmed, 10);
  if (!Number.isNaN(asInt) && /^-?\d+$/.test(trimmed)) {
    return Math.max(0, asInt);
  }
  const m = /^(\d+)d(\d+)$/i.exec(trimmed);
  if (!m) return 0;
  const count = Number.parseInt(m[1]!, 10);
  const sides = Number.parseInt(m[2]!, 10);
  if (count <= 0 || sides <= 0) return 0;
  let sum = 0;
  for (let i = 0; i < count; i++) sum += rng(1, sides);
  return sum;
}
